import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Send as SendIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSendTestMessage } from '../hooks/useWhatsAppConfig';

interface StepTestingProps {
  formData: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onBack?: () => void;
  isActive: boolean;
}

interface TestMessage {
  to_number: string;
  message: string;
  timestamp: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  response_received?: string;
  response_time?: number;
}

const testSchema = yup.object({
  test_number: yup
    .string()
    .matches(/^\+?1?\d{9,15}$/, 'Format de numéro invalide')
    .required('Le numéro de test est requis'),
  test_message: yup
    .string()
    .min(1, 'Le message ne peut pas être vide')
    .max(1000, 'Le message est trop long')
    .required('Le message de test est requis'),
});

const predefinedMessages = [
  'Bonjour, je teste mon agent FAQ WhatsApp',
  'Comment puis-je vous aider ?',
  'Test de fonctionnement',
  'Quels sont vos horaires d\'ouverture ?',
];

export const StepTesting: React.FC<StepTestingProps> = ({
  formData,
  onComplete,
  onNext,
  onBack,
  isActive,
}) => {
  const [testMessages, setTestMessages] = useState<TestMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  
  const sendTestMessage = useSendTestMessage();

  const formik = useFormik({
    initialValues: {
      test_number: formData?.test_number || '',
      test_message: formData?.test_message || predefinedMessages[0],
    },
    validationSchema: testSchema,
    onSubmit: async (values) => {
      setIsSending(true);
      
      try {
        // Créer un message de test temporaire
        const tempMessage: TestMessage = {
          to_number: values.test_number,
          message: values.test_message,
          timestamp: new Date().toISOString(),
          status: 'pending',
        };
        
        setTestMessages(prev => [tempMessage, ...prev]);
        
        if (!formData?.config_id) {
          throw new Error('Créez d’abord la configuration avant de lancer le test.');
        }

        const sentMessage = await sendTestMessage.mutateAsync({
          configId: formData.config_id,
          testData: {
            to_number: values.test_number,
            message: values.test_message,
          },
        });

        const successMessage: TestMessage = {
          ...tempMessage,
          ...sentMessage,
          response_time: 0,
        };
        
        setTestMessages(prev => prev.map((msg, index) => 
          index === 0 ? successMessage : msg
        ));
        
      } catch (error: any) {
        // Mettre à jour le message avec l'erreur
        setTestMessages(prev => prev.map((msg, index) => 
          index === 0 ? { ...msg, status: 'failed' } : msg
        ));
      } finally {
        setIsSending(false);
      }
    },
  });

  const getStatusIcon = (status: TestMessage['status']) => {
    switch (status) {
      case 'pending':
        return <CircularProgress size={20} />;
      case 'sent':
        return <ScheduleIcon color="info" />;
      case 'delivered':
        return <CheckIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const getStatusChip = (status: TestMessage['status']) => {
    switch (status) {
      case 'pending':
        return <Chip label="Envoi..." color="default" size="small" />;
      case 'sent':
        return <Chip label="Envoyé" color="info" size="small" />;
      case 'delivered':
        return <Chip label="Livré" color="success" size="small" />;
      case 'failed':
        return <Chip label="Échec" color="error" size="small" />;
      default:
        return null;
    }
  };

  const handleCompleteStep = () => {
    const testData = {
      test_number: formik.values.test_number,
      test_messages: testMessages,
      testing_completed: true,
    };
    onComplete(testData);
  };

  const skipTesting = () => {
    onComplete({ testing_completed: false, testing_skipped: true });
  };

  if (!isActive) return null;

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={3}>
        {/* Information */}
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Testez votre configuration en envoyant un message WhatsApp à votre propre numéro.
              Cela vérifie que l'agent FAQ fonctionne correctement.
            </Typography>
          </Alert>
        </Grid>

        {/* Formulaire de test */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SendIcon color="primary" />
                Envoyer un message de test
              </Typography>

              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  {/* Numéro de test */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Numéro de test"
                      name="test_number"
                      value={formik.values.test_number}
                      onChange={formik.handleChange}
                      error={formik.touched.test_number && Boolean(formik.errors.test_number)}
                      helperText={formik.touched.test_number && formik.errors.test_number}
                      placeholder="+22507123456"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Message de test */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message de test"
                      name="test_message"
                      value={formik.values.test_message}
                      onChange={formik.handleChange}
                      error={formik.touched.test_message && Boolean(formik.errors.test_message)}
                      helperText={formik.touched.test_message && formik.errors.test_message}
                      multiline
                      rows={3}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <MessageIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Messages prédéfinis */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Messages suggérés :
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {predefinedMessages.map((msg, index) => (
                        <Chip
                          key={index}
                          label={msg}
                          variant="outlined"
                          size="small"
                          onClick={() => formik.setFieldValue('test_message', msg)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Bouton d'envoi */}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSending || !formik.isValid}
                      startIcon={isSending ? <CircularProgress size={20} /> : <SendIcon />}
                      sx={{ mt: 1 }}
                    >
                      {isSending ? 'Envoi en cours...' : 'Envoyer le test'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Historique des tests */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                📋 Historique des tests
              </Typography>
              
              {testMessages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucun test effectué pour le moment.
                </Typography>
              ) : (
                <List dense>
                  {testMessages.map((msg, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemIcon>
                        {getStatusIcon(msg.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" noWrap>
                              {msg.to_number}
                            </Typography>
                            {getStatusChip(msg.status)}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              {msg.message.substring(0, 50)}...
                            </Typography>
                            {msg.response_received && (
                              <Typography variant="caption" color="success.main" display="block">
                                ✓ Réponse reçue
                              </Typography>
                            )}
                            {msg.response_time && (
                              <Typography variant="caption" color="text.secondary">
                                {msg.response_time}s
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            {onBack && (
              <Button onClick={onBack} disabled={isSending}>
                Retour
              </Button>
            )}
            
            <Button
              variant="outlined"
              onClick={skipTesting}
              disabled={isSending}
            >
              Ignorer les tests
            </Button>
            
            <Button
              variant="contained"
              onClick={handleCompleteStep}
              disabled={isSending}
              sx={{ minWidth: 120 }}
            >
              Terminer
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
